import { TokenCard, TokenCardRef } from "@almafintech/react-components";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../app/hooks";
import {
  requestEmailVerification,
  verifyEmail,
} from "../../connectors/interactiveBrokers";
import { updateToken } from "../../reducers/authReducer";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
} from "../../reducers/buttonsReducer";
import { maskEmail, minutesToMiliseconds } from "../../utils";
import { texts } from "./i18n";

// TODO: Merge with SecuritiesTokenStep (and maybe TokenStep too while at it)

interface Props {
  email: string;
  language: "es" | "en";
  onBackClicked?: () => void;
  onAuthorizationSuccesful?: () => void;
}

const InteractiveBrokersToken = (props: Props) => {
  const {
    email,
    onBackClicked: handleBackClicked,
    onAuthorizationSuccesful: handleAuthorizationSuccesful,
    language,
  } = props;

  const tokenCardRef = useRef<TokenCardRef>(null);
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [lastTokenSent, setLastTokenSent] = useState<number>(Date.now());
  const [isSuccess, setIsSuccess] = useState(false);

  const handleAuthorizeClicked = () => {
    if (tokenCardRef.current) {
      tokenCardRef?.current?.authorizeToken();
    }
  };

  const handleAuthorize = async (token: string) => {
    setLoading(true);

    try {
      const {
        data: { token: jwtToken },
      } = await verifyEmail({ email, token });

      dispatch(updateToken(jwtToken));

      setIsSuccess(true);
      return {
        success: true,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return {
        success: false,
        message: texts[language].errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await requestEmailVerification({ email, language_preference: language });
      setLastTokenSent(Date.now());
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    dispatch(
      onUpdatePrimaryButton({
        onClick: handleAuthorizeClicked,
        text: texts[language].primaryButton,
        type: "button",
        disabled: loading,
        isLoading: loading,
        show: !isSuccess,
      })
    );

    dispatch(
      onUpdateSecondaryButton({
        onClick: handleBackClicked,
        text: texts[language].secondaryButton,
        disabled: false,
        isLoading: false,
        show: !isSuccess,
      })
    );
  }, [loading, isSuccess]);

  return (
    <TokenCard
      ref={tokenCardRef}
      title={texts[language].title}
      subtitle={texts[language].subtitle}
      lastSendToken={lastTokenSent + minutesToMiliseconds(2)}
      resendText={texts[language].resendText}
      incorrectTokenErrorMessage={texts[language].incorrectTokenErrorMessage}
      lengthValidationErrorMessage={
        texts[language].lengthValidationErrorMessage
      }
      onSuccess={() => handleAuthorizationSuccesful?.()}
      onAuthorize={handleAuthorize}
      onResend={handleResend}
    >
      {texts[language].resendText} {maskEmail(email)}
    </TokenCard>
  );
};

export default InteractiveBrokersToken;
